import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AudioElement,
    type ColorParams,
    ElementTypes,
    type MultiPageVideoModel,
    type SolidColor,
    StrokeType,
    VideoElement,
} from '@bynder-studio/render-core';
import { Form, List, Thumbnail } from '@bynder/design-system';
import { IconColor, IconCaretDown, IconCaretUp } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { EditorColor } from 'packages/pages/editor/RightSideMenu/FormComponents/EditorColor';
import { FontFamilyBase, FontSizeBase, FontStyleBase } from 'packages/pages/editor/RightSideMenu/FormComponents/Fonts';
import useForceUpdate from '~/hooks/useForceUpdate';
import { StyledDivider } from 'packages/pages/components/Transcript/Component.styled';

type TranscriptStylesProps = {
    creativeModel: MultiPageVideoModel;
};

const TranscriptStyles = ({ creativeModel }: TranscriptStylesProps) => {
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);
    const forceUpdate = useForceUpdate();

    const transcriptElement = creativeModel.getTranscriptElement();

    const handleToggle = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);

    const fontIdData = useMemo(
        (): [string, boolean, string] => [String(transcriptElement.fontId), false, ''],
        [transcriptElement.fontId],
    );

    const rawFontIds = useMemo((): string[] => [String(transcriptElement.fontId)], [transcriptElement.fontId]);

    const fontSizeData: [number, boolean, string] = useMemo(
        () => [transcriptElement.fontSize, false, ''],
        [transcriptElement.fontSize],
    );

    const fontStyleData: [string, boolean, string] = useMemo(
        () => [String(transcriptElement.fontId || 'default'), false, ''],
        [transcriptElement.fontId],
    );

    const hasTranscribableMedia = useMemo(() => {
        const currentModel = creativeModel.getCurrentModel?.();

        if (!currentModel) {
            return false;
        }

        const elements = currentModel.getAllElementsRecursively?.() || currentModel.getElements?.() || [];

        return elements.some((element: any) => {
            return element instanceof VideoElement || element instanceof AudioElement;
        });
    }, [creativeModel]);

    const handleFontChange = useCallback(
        (fontId: string) => {
            creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, { fontId });
            forceUpdate();
        },
        [creativeModel, forceUpdate],
    );

    const handleFontStyleChange = useCallback(
        (fontId: string) => {
            creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, { fontId });
            forceUpdate();
        },
        [creativeModel, forceUpdate],
    );

    const handleFontSizeChange = useCallback(
        (fontSize: number) => {
            creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, { fontSize });
        },
        [creativeModel],
    );

    const handleTextColorChange = useCallback(
        (_property: string, value: ColorParams) => {
            creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, { color: value });
            forceUpdate();
        },
        [creativeModel, forceUpdate],
    );

    const handleBackgroundColorChange = useCallback(
        (_property: string, value: ColorParams) => {
            creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, {
                background: { ...transcriptElement.background, color: value as SolidColor },
            });
            forceUpdate();
        },
        [creativeModel, forceUpdate, transcriptElement.background],
    );

    const isBackgroundEnabled = transcriptElement.background?.enabled ?? false;

    const handleToggleBackground = useCallback(() => {
        creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, {
            background: { ...transcriptElement.background, enabled: !isBackgroundEnabled },
        });
        forceUpdate();
    }, [creativeModel, forceUpdate, transcriptElement.background, isBackgroundEnabled]);

    const isStrokeEnabled = transcriptElement.stroke?.enabled ?? transcriptElement.stroke?.type !== StrokeType.NONE;

    const handleToggleStroke = useCallback(() => {
        creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, {
            stroke: {
                ...transcriptElement.stroke,
                enabled: !isStrokeEnabled,
                type: isStrokeEnabled ? StrokeType.NONE : StrokeType.CENTER,
                weight: !isStrokeEnabled ? 0 : 1,
            },
        });
        forceUpdate();
    }, [creativeModel, forceUpdate, transcriptElement.stroke, isStrokeEnabled]);

    const handleStrokeColorChange = useCallback(
        (_property: string, value: ColorParams) => {
            creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, {
                stroke: { ...transcriptElement.stroke, color: value as SolidColor },
            });
            forceUpdate();
        },
        [creativeModel, forceUpdate, transcriptElement.stroke],
    );

    return (
        <>
            <List>
                <List.Item
                    thumbnail={<Thumbnail variant="clean" icon={<IconColor />} />}
                    isChecked={isOpen}
                    rightElements={!isOpen ? <IconCaretDown /> : <IconCaretUp />}
                    onClick={handleToggle}
                    isDisabled={!transcriptElement || !hasTranscribableMedia}
                >
                    {translate('editor.transcript.styles.title')}
                </List.Item>
            </List>
            {isOpen && (
                <Form>
                    <Form.Group>
                        <Form.Label>{translate('editor.transcript.styles.font')}</Form.Label>
                        <FontFamilyBase
                            fontIdData={fontIdData}
                            rawFontIds={rawFontIds}
                            onFontChange={handleFontChange}
                            locked={transcriptElement.locked}
                        />
                        <Form.Row>
                            <FontStyleBase
                                fontStyleData={fontStyleData}
                                fontId={String(transcriptElement?.fontId || 'default')}
                                onFontStyleChange={handleFontStyleChange}
                                locked={transcriptElement.locked}
                            />
                            <FontSizeBase
                                fontSizeData={fontSizeData}
                                onFontSizeChange={handleFontSizeChange}
                                locked={transcriptElement.locked}
                            />
                        </Form.Row>
                    </Form.Group>
                    <StyledDivider />
                    <Form.Group>
                        <Form.Label>{translate('editor.transcript.styles.text-color')}</Form.Label>
                        <EditorColor
                            property="color"
                            testId="transcript-text-color"
                            selectedElement={transcriptElement}
                            color={transcriptElement.color}
                            colorTypes={['SOLID']}
                            onChange={handleTextColorChange}
                            disabled={false}
                        />
                    </Form.Group>
                    <StyledDivider />
                    <Form.Group>
                        <List.Item checkedVariant="switch" onClick={handleToggleStroke} isChecked={isStrokeEnabled}>
                            {translate('editor.transcript.styles.stroke-color')}
                        </List.Item>
                        {isStrokeEnabled && (
                            <EditorColor
                                property="strokeColor"
                                testId="transcript-stroke-color"
                                selectedElement={transcriptElement}
                                color={transcriptElement.stroke.color}
                                colorTypes={['SOLID']}
                                onChange={handleStrokeColorChange}
                                disabled={false}
                            />
                        )}
                    </Form.Group>
                    <StyledDivider />
                    <Form.Group>
                        <List.Item
                            checkedVariant="switch"
                            onClick={handleToggleBackground}
                            isChecked={isBackgroundEnabled}
                        >
                            {translate('editor.transcript.styles.background-color')}
                        </List.Item>
                        {isBackgroundEnabled && (
                            <EditorColor
                                property="backgroundColor"
                                testId="transcript-bg-color"
                                selectedElement={transcriptElement}
                                color={transcriptElement.background.color}
                                colorTypes={['SOLID']}
                                onChange={handleBackgroundColorChange}
                                disabled={false}
                            />
                        )}
                    </Form.Group>
                </Form>
            )}
        </>
    );
};

export default TranscriptStyles;
